// Frame orchestration for the Canvas2D arena (architecture §9.2, visuals §12):
//  1 background fill;  2 grid (classic: one 50 u world grid; sketch: paper, 50/250 u grid, border);
//  3 food (live, batched by colour);  4 eaten food (fading);  5 ejected mass, cells, viruses and
//  spawners in one list sorted by **drawn** radius ascending, each cell followed immediately by
//  its name / mass labels;  6 BR zone (M3);  7 minimap (when on);
//  8 the full-screen dim overlay (last). While the static boot scene is on (visuals §11.1), steps
//  1–7 are one blit of the cached scene.
//
// Display-only animation lives here, on top of the sampled view: drawn-radius easing (158 ms
// half-life), the eat fade (α 1 − t, r × (1 − 0.3 t)), classic pellets' size mapping and no pop-in
// (render/display.ts), and the jelly soft bodies (render/jelly.ts).
//
// Styles (owner override; `settings.style`):
// - 'classic' (default): agar's look (visuals.md): #F2FBFF / #111 background, translucent 50 u
//   grid, no border or outside shade, 10 u two-fill bands on jelly polygons, generated colours,
//   Ubuntu names, 50-spike green viruses;
// - 'sketch': the spec's paper look with the boiling ink outline (architecture §9.3).
// - `settings.darkMode` picks the dark variant of either style.
//
// Implementer: client render. Consumers: app.ts (one Renderer for the #game canvas), tests (the
// `stats` counters are the M2 "settings take effect" spies).

import {
  classicOutlineColor,
  KIND,
  MATCH_STATE,
  MODE_CODE,
  type Rgb,
  scaleRgb,
  type WelcomeMessage,
  worldHalfSize,
} from '@ballio/shared';
import { type AssetImage, type AssetLoader } from '../assets/loader.js';
import { PELLET_VARIANTS } from '../assets/slots.js';
import type { Camera } from '../game/camera.js';
import { ENTITY_EFFECT, type EntityRecord, type WorldStore } from '../game/world-store.js';
import type { Settings } from '../settings/store.js';
import { BackgroundPainter } from './background.js';
import { BootScene } from './boot-scene.js';
import { type Ctx2D, get2d } from './canvas.js';
import { CellPainter, type CellLook, MIN_SKIN_RADIUS_PX } from './cells.js';
import { circleInRect, makeViewRect, type ViewRect, viewWorldRect } from './cull.js';
import { baseRadius, displayAlpha, easesRadius, effectRadius, RadiusEaser } from './display.js';
import { drawEjectedOne, ejectedVisible } from './ejected.js';
import { type FoodArt, foodDrawRadius, FoodPainter } from './food.js';
import { loadCanvasFonts } from './fonts.js';
import { type Frame, useScreenTransform, useWorldTransform } from './frame.js';
import { JELLY, type JellyBody, JellyField, jellyVertexCount } from './jelly.js';
import { LabelPainter, nameReachU, ownMassScale } from './labels.js';
import { MinimapPainter, minimapShown } from './minimap.js';
import { boilFrameAt, MAX_OUTLINE_VERTICES } from './outline.js';
import { DimOverlay } from './overlay.js';
import type { RenderQuality } from './quality.js';
import { drawSpawner } from './spawners.js';
import { SpriteCache } from './sprite-cache.js';
import { TextCache } from './text-cache.js';
import { type ArenaTheme, resolveTheme } from './theme.js';
import { CLASSIC_VIRUS, CLASSIC_VIRUS_VERTICES, drawVirus, type SpikeArt } from './viruses.js';
import { drawZone } from './zone.js';

// ---------------------------------------------------------------------------------------------
// Graphics levels (ui-flow §4.5): defined in render/quality.ts (with the Auto controller).
// ---------------------------------------------------------------------------------------------

export {
  AUTO_QUALITY,
  autoStartLevel,
  QUALITY_LEVELS,
  QualityController,
  type QualityLevel,
  qualityProfile,
  type RenderQuality,
} from './quality.js';
export { DIM_RATES, MENU_DIM } from './overlay.js';

/** Per-frame draw counters (reset by every `draw`, except `frames`, which counts draws). */
export interface RenderStats {
  frames: number;
  food: number;
  ejected: number;
  bodies: number;
  nameDraws: number;
  massDraws: number;
  skinDraws: number;
  jellyDraws: number;
}

export interface RendererOptions {
  readonly assets?: AssetLoader | null;
  readonly textCache?: TextCache;
  /** Tinted sprites and skin mips (default: a private cache). */
  readonly spriteCache?: SpriteCache;
  /**
   * Start with the static boot scene on (visuals §11.1, D-U3). It switches itself off when the
   * first own cell is drawn; the app turns it off on spectate with `setBootScene(false)`.
   * Default false (the live arena).
   */
  readonly bootScene?: boolean;
  /** Register and load the bundled Ubuntu faces (default true; render/fonts.ts). */
  readonly loadFonts?: boolean;
  /** Where public/fonts is served (default `/fonts/`). */
  readonly fontsBaseUrl?: string;
}

/** Sketch outline colour: the cell colour darkened to 72 % (architecture §9.3). */
export function sketchOutlineColor(c: Rgb): Rgb {
  return scaleRgb(c, 0.72);
}

/** Cells are drawn at this opacity while Battle Royale players are ghosts (ui-flow §6.8). */
export const GHOST_ALPHA = 0.5;

/** True during the Battle Royale lobby and countdown, when every cell is a ghost. */
export function ghostsActive(store: Pick<WorldStore, 'info' | 'match'>): boolean {
  const state = store.match.value?.state;
  return (
    store.info?.mode === MODE_CODE.battleroyale &&
    (state === MATCH_STATE.LOBBY || state === MATCH_STATE.COUNTDOWN)
  );
}

/** World bounds from WELCOME, or the default map before it arrives. */
export function mapBoundsOf(info: WelcomeMessage | null, out: ViewRect = makeViewRect()): ViewRect {
  if (info && info.maxX > info.minX && info.maxY > info.minY) {
    out.minX = info.minX;
    out.minY = info.minY;
    out.maxX = info.maxX;
    out.maxY = info.maxY;
  } else {
    const h = worldHalfSize();
    out.minX = -h;
    out.minY = -h;
    out.maxX = h;
    out.maxY = h;
  }
  return out;
}

/** One body as drawn this frame (pooled): the record plus its display position, radius, alpha. */
interface Drawn extends JellyBody {
  rec: EntityRecord;
  id: number;
  kind: number;
  x: number;
  y: number;
  r: number;
  alpha: number;
}

function newDrawn(): Drawn {
  return {
    rec: null as unknown as EntityRecord,
    id: 0,
    kind: 0,
    x: 0,
    y: 0,
    r: 0,
    alpha: 1,
  };
}

function byDrawnRadius(a: Drawn, b: Drawn): number {
  return a.r - b.r || a.id - b.id;
}

interface CachedArt {
  version: number;
  loader: AssetLoader | null;
  food: FoodArt | null;
  spikes: SpikeArt;
  ejected: AssetImage | null;
  ejectedTint: boolean;
  overlay: AssetImage | null;
  paperLight: AssetImage | null;
  paperDark: AssetImage | null;
}

const NO_SPIKE_ART: SpikeArt = Object.freeze({ virus: null, virusFed: null, spawner: null });

/** Pellets within this many world units of the view take part in the jelly contacts. */
const JELLY_FOOD_MARGIN_U = 120;

export class Renderer {
  readonly canvas: HTMLCanvasElement;
  /** This frame's own-cell mass label factor (`ownMassScale`); 0 = none. */
  private ownMassScale = 0;
  readonly stats: RenderStats = {
    frames: 0,
    food: 0,
    ejected: 0,
    bodies: 0,
    nameDraws: 0,
    massDraws: 0,
    skinDraws: 0,
    jellyDraws: 0,
  };
  protected readonly opts: RendererOptions;
  /** The name and mass bitmap cache (shared with the app so it can clear it on font load). */
  readonly textCache: TextCache;
  readonly sprites: SpriteCache;
  /** The jelly soft-body state (tests, stats). */
  readonly jelly = new JellyField();
  /** Drawn radii (eased toward the sampled radius). */
  readonly radii = new RadiusEaser();
  private readonly ctx: Ctx2D | null;
  private cssW: number;
  private cssH: number;
  private bs = 1;
  private boil = 0;
  private theme: ArenaTheme | null = null;
  private themeSource: unknown = undefined;
  private readonly view = makeViewRect();
  private readonly map = makeViewRect();
  private readonly jellyView = makeViewRect();
  private readonly background = new BackgroundPainter();
  private readonly food = new FoodPainter();
  private readonly cells = new CellPainter();
  private readonly labels = new LabelPainter();
  private readonly minimap = new MinimapPainter();
  private readonly overlay = new DimOverlay();
  private readonly boot = new BootScene();
  private bootOn: boolean;
  private readonly drawn: Drawn[] = [];
  private readonly drawnPool: Drawn[] = [];
  private drawnCount = 0;
  private readonly jellyBodies: Drawn[] = [];
  private readonly foodDrawn: Drawn[] = [];
  private frame: Frame | null = null;
  private art: CachedArt | null = null;
  private lastNowMs = Number.NaN;
  private readonly motionQuery: { readonly matches: boolean } | null;

  constructor(canvas: HTMLCanvasElement, opts: RendererOptions = {}) {
    this.canvas = canvas;
    this.opts = opts;
    this.textCache = opts.textCache ?? new TextCache();
    this.sprites = opts.spriteCache ?? new SpriteCache();
    this.ctx = get2d(canvas, { alpha: false });
    this.cssW = Math.max(1, canvas.width || 1);
    this.cssH = Math.max(1, canvas.height || 1);
    this.bootOn = opts.bootScene === true;
    let mq: { matches: boolean } | null;
    try {
      mq =
        typeof globalThis.matchMedia === 'function'
          ? globalThis.matchMedia('(prefers-reduced-motion: reduce)')
          : null;
    } catch {
      mq = null;
    }
    this.motionQuery = mq;
    if (opts.loadFonts !== false) {
      void loadCanvasFonts(opts.fontsBaseUrl).then((ok) => {
        // Names cached before the face arrived were drawn in a fallback face.
        if (ok) this.textCache.clear();
      });
    }
  }

  /** The current boil frame (advances every 125 ms; frozen at Medium and below and with Reduce motion). */
  get boilFrame(): number {
    return this.boil;
  }

  /** Backing px per CSS px, as set by the last `resize`. */
  get backingScale(): number {
    return this.bs;
  }

  /** The canvas dim overlay's current opacity (visuals §11). */
  get dim(): number {
    return this.overlay.dim;
  }

  /** The dim overlay's target opacity. */
  get dimTarget(): number {
    return this.overlay.target;
  }

  /**
   * Eases the full-screen dim linearly toward `target` (0–1) at `ratePerSec` opacity per second
   * (≤ 0 = at once). See DIM_RATES / MENU_DIM for the agar timings (visuals §11, ui-qol §3).
   */
  setDim(target: number, ratePerSec: number): void {
    this.overlay.setDim(target, ratePerSec);
  }

  /** True while the static boot scene replaces the arena (visuals §11.1). */
  get bootScene(): boolean {
    return this.bootOn;
  }

  /**
   * Turns the static boot scene on or off. It turns itself off when the first own cell is
   * drawn; turn it off on spectate start. Once off it is meant to stay off for the page load.
   */
  setBootScene(on: boolean): void {
    this.bootOn = on;
    if (!on) this.boot.release();
  }

  /** Canvas CSS size and backing scale (call on resize and on graphics-level change). */
  resize(cssWidth: number, cssHeight: number, backingScale: number): void {
    const s = backingScale > 0 && Number.isFinite(backingScale) ? backingScale : 1;
    const w = Math.max(1, Number.isFinite(cssWidth) ? cssWidth : 1);
    const h = Math.max(1, Number.isFinite(cssHeight) ? cssHeight : 1);
    const bw = Math.max(1, Math.round(w * s));
    const bh = Math.max(1, Math.round(h * s));
    if (this.canvas.width !== bw) this.canvas.width = bw;
    if (this.canvas.height !== bh) this.canvas.height = bh;
    this.cssW = w;
    this.cssH = h;
    this.bs = s;
  }

  /**
   * Draws one frame from `store.view` (call `store.sample(rt)` and `camera.update(...)` first),
   * `store.players` / `store.info` / `store.own` / `store.money`, the settings (style, darkMode,
   * hideSkins, hideNames, plainColors, showMass, showMinimap, labelCurrency, reduceMotion) and
   * the quality level. `nowMs` (performance.now) drives the easing, the jelly sub-steps, the dim
   * and the cash-out ring animation.
   */
  draw(
    camera: Camera,
    store: WorldStore,
    settings: Settings,
    quality: RenderQuality,
    nowMs: number,
  ): void {
    const st = this.stats;
    st.frames++;
    st.food = 0;
    st.ejected = 0;
    st.bodies = 0;
    st.nameDraws = 0;
    st.massDraws = 0;
    st.skinDraws = 0;
    st.jellyDraws = 0;
    const dtRaw = Number.isFinite(this.lastNowMs) ? nowMs - this.lastNowMs : 1000 / 60;
    const dtMs = dtRaw > 0 ? Math.min(dtRaw, 1000) : 0;
    this.lastNowMs = nowMs;
    this.overlay.step(dtMs);
    const ctx = this.ctx;
    if (!ctx) return;

    const theme = this.themeFor(settings);
    const reduceMotion = settings.reduceMotion ?? this.motionQuery?.matches ?? false;
    if (quality.boil && !reduceMotion) this.boil = boilFrameAt(nowMs);
    const zoom = camera.zoom > 0 && Number.isFinite(camera.zoom) ? camera.zoom : 1;
    const camX = Number.isFinite(camera.x) ? camera.x : 0;
    const camY = Number.isFinite(camera.y) ? camera.y : 0;
    viewWorldRect(camX, camY, zoom, this.cssW, this.cssH, 0, this.view);
    mapBoundsOf(store.info, this.map);

    const f = this.frameFor(ctx, theme, quality, settings);
    f.bs = this.bs;
    f.zoom = zoom;
    f.scale = zoom * this.bs;
    f.offX = this.bs * (this.cssW / 2 - camX * zoom);
    f.offY = this.bs * (this.cssH / 2 - camY * zoom);
    f.width = this.canvas.width;
    f.height = this.canvas.height;
    f.theme = theme;
    f.quality = quality;
    f.settings = settings;
    f.reduceMotion = reduceMotion;
    f.nowMs = nowMs;
    f.dtMs = dtMs;
    f.boilFrame = this.boil;

    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
    ctx.setLineDash([]);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'medium';

    if (this.bootOn && store.view.own.length > 0) this.setBootScene(false);
    if (this.bootOn) {
      this.boot.draw(ctx, this.cssW, this.cssH, this.bs, theme);
      this.overlay.draw(ctx, f.width, f.height);
      return;
    }

    const art = this.artFor();
    const view = store.view;
    this.ownMassScale = ownMassScale(store.own.score, view.own);
    this.background.draw(
      f,
      theme.style === 'sketch' ? (theme.dark ? art.paperDark : art.paperLight) : null,
    );
    const jellyOn = theme.style !== 'sketch' && quality.jelly && !quality.perfectCircles;
    this.prepareBodies(f, store, jellyOn);
    this.food.draw(f, view.food, art.food, this.sprites);
    this.drawBodies(f, store, art, jellyOn);
    const zone = store.match.value?.zone;
    if (zone) drawZone(f, zone);
    useScreenTransform(f);
    if (minimapShown(settings, store)) this.minimap.draw(f, store, settings, this.cssW, this.cssH);

    this.labels.endFrame(nowMs);
    this.radii.sweep(nowMs);
    if (jellyOn) this.jelly.endFrame();
    this.overlay.draw(ctx, f.width, f.height);
    useScreenTransform(f);
    ctx.globalAlpha = 1;
  }

  /**
   * Fills `this.drawn` (ejected + cells + viruses + spawners with their eased, effect-applied
   * radii, sorted by drawn radius) and, with jelly on, the frame's contact bodies.
   */
  private prepareBodies(f: Frame, store: WorldStore, jellyOn: boolean): void {
    const view = store.view;
    const now = f.nowMs;
    const dt = f.dtMs;
    let n = 0;
    const add = (rec: EntityRecord): void => {
      if (!(rec.r > 0) && rec.effect === 0) return;
      let d = this.drawn[n];
      if (!d) {
        d = this.drawnPool.pop() ?? newDrawn();
        this.drawn[n] = d;
      }
      const base = baseRadius(rec, false);
      const eased = easesRadius(rec.kind) ? this.radii.ease(rec.id, rec.kind, base, now, dt) : base;
      d.rec = rec;
      d.id = rec.id;
      d.kind = rec.kind;
      d.x = rec.x;
      d.y = rec.y;
      d.r = effectRadius(rec, eased);
      d.alpha = displayAlpha(rec);
      n++;
    };
    for (let i = 0; i < view.ejected.length; i++) add(view.ejected[i] as EntityRecord);
    for (let i = 0; i < view.bodies.length; i++) add(view.bodies[i] as EntityRecord);
    this.drawnCount = n;
    const list = this.drawn;
    if (list.length > n) {
      for (let i = n; i < list.length; i++) this.drawnPool.push(list[i] as Drawn);
      list.length = n;
    }
    list.sort(byDrawnRadius);

    if (!jellyOn) return;
    // Contact bodies: everything drawn plus the pellets near the view (drawn radii). A body being
    // eaten is left out: it slides into its eater, which must not dent around it (and it draws
    // as a plain shrinking circle, visuals §9.1).
    const bodies = this.jellyBodies;
    let m = 0;
    for (let i = 0; i < n; i++) {
      const d = list[i] as Drawn;
      if (d.rec.effect !== ENTITY_EFFECT.EATEN) bodies[m++] = d;
    }
    const jv = this.jellyView;
    const v = f.view;
    jv.minX = v.minX - JELLY_FOOD_MARGIN_U;
    jv.minY = v.minY - JELLY_FOOD_MARGIN_U;
    jv.maxX = v.maxX + JELLY_FOOD_MARGIN_U;
    jv.maxY = v.maxY + JELLY_FOOD_MARGIN_U;
    const food = view.food;
    let j = 0;
    // Pellets only matter next to a jelly body big enough on screen; skip them when zoomed out
    // so far that every dent would be sub-pixel.
    if (f.zoom * JELLY.kBody * 10 >= 0.25) {
      for (let i = 0; i < food.length; i++) {
        const rec = food[i] as EntityRecord;
        if (!circleInRect(rec.x, rec.y, 30, jv)) continue;
        let d = this.foodDrawn[j];
        if (!d) {
          d = newDrawn();
          this.foodDrawn[j] = d;
        }
        j++;
        bodies[m] = d;
        d.rec = rec;
        d.id = rec.id;
        d.kind = rec.kind;
        d.x = rec.x;
        d.y = rec.y;
        d.r = foodDrawRadius(rec, true);
        d.alpha = 1;
        m++;
      }
    }
    this.jelly.beginFrame(f.nowMs, bodies, m, f.map);
  }

  private drawBodies(f: Frame, store: WorldStore, art: CachedArt, jellyOn: boolean): void {
    const list = this.drawn;
    const n = this.drawnCount;
    useWorldTransform(f);
    const ghost = ghostsActive(store) ? GHOST_ALPHA : 1;
    const plain = f.settings.plainColors;
    for (let i = 0; i < n; i++) {
      const d = list[i] as Drawn;
      if (!(d.r > 0) || !(d.alpha > 0.004)) continue;
      const rec = d.rec;
      switch (rec.kind) {
        case KIND.CELL:
          this.drawCell(f, store, d, art, ghost, jellyOn);
          break;
        case KIND.EJECTED: {
          if (!ejectedVisible(f, d, art.ejected)) break;
          const rPx = d.r * f.zoom;
          const jn = jellyOn && rPx >= JELLY.minRadiusPx ? jellyVertexCount(rPx) : 0;
          const off = jn > 0 ? this.jelly.offsets(d.id, d.x, d.y, d.r, jn) : null;
          if (off) f.stats.jellyDraws++;
          f.ctx.globalAlpha = d.alpha;
          drawEjectedOne(
            f,
            d,
            plain ? f.theme.plain : rec.color,
            off,
            jn,
            art.ejected,
            art.ejectedTint,
            this.sprites,
          );
          break;
        }
        case KIND.VIRUS: {
          const rPx = d.r * f.zoom;
          const on = jellyOn && rPx >= JELLY.minRadiusPx && !art.spikes.virus;
          const off = on
            ? this.jelly.offsets(d.id, d.x, d.y, d.r, CLASSIC_VIRUS_VERTICES, {
                noise: f.reduceMotion ? 0 : CLASSIC_VIRUS.noiseU,
              })
            : null;
          if (off) f.stats.jellyDraws++;
          f.ctx.globalAlpha = d.alpha;
          drawVirus(f, rec, store.info, art.spikes, d, off);
          break;
        }
        case KIND.SPAWNER:
          f.ctx.globalAlpha = d.alpha;
          drawSpawner(f, d, art.spikes);
          break;
        default:
          break;
      }
    }
    f.ctx.globalAlpha = 1;
  }

  private drawCell(
    f: Frame,
    store: WorldStore,
    d: Drawn,
    art: CachedArt,
    ghost: number,
    jellyOn: boolean,
  ): void {
    const rec = d.rec;
    const r = d.r;
    const rPx = r * f.zoom;
    if (rPx < 0.3) return;
    const s = f.settings;
    const player = store.players.get(rec.playerId);
    const name = player?.name ?? '';
    let reach = r + 5;
    if (!s.hideNames && name !== '') reach = Math.max(reach, nameReachU(r, name.length));
    if (!circleInRect(d.x, d.y, reach, f.view)) return;
    f.stats.bodies++;

    const isOwn =
      player?.isYou === true || (rec.playerId !== 0 && rec.playerId === store.own.playerId);
    const plain = s.plainColors && !isOwn && player?.teammate !== true;
    const fill = plain ? f.theme.plain : rec.color;
    const assets = this.opts.assets ?? null;
    let skin: AssetImage | null = null;
    let border: Rgb | null = null;
    const skinId = player?.skinId ?? '';
    if (!s.hideSkins && skinId !== '' && assets) {
      const img = assets.skin(skinId); // also starts the lazy load the first time
      if (img && rPx >= Math.max(MIN_SKIN_RADIUS_PX, f.quality.minSkinRadiusPx)) {
        skin = img;
        border = assets.skinBorder(skinId);
      }
    }
    const look: CellLook = {
      fill,
      border:
        border ??
        (f.theme.style === 'sketch' ? sketchOutlineColor(fill) : classicOutlineColor(fill)),
      skin,
      overlay: skin ? null : art.overlay,
    };
    // visuals §9.1: an eaten cell is slide + ×(1 − 0.3t) + fade; no jelly (the eater's body
    // would dent it all round as it slides in).
    const eaten = rec.effect === ENTITY_EFFECT.EATEN;
    const jn = jellyOn && !eaten && rPx >= JELLY.minRadiusPx ? jellyVertexCount(rPx) : 0;
    const off =
      jn > 0
        ? this.jelly.offsets(d.id, d.x, d.y, r, Math.min(jn, MAX_OUTLINE_VERTICES), {
            noise: f.reduceMotion ? 0 : undefined,
          })
        : null;
    if (off) f.stats.jellyDraws++;
    const alpha = ghost * d.alpha;
    f.ctx.globalAlpha = alpha;
    this.cells.drawBody(f, d, look, off, jn, this.sprites);

    const showName = !s.hideNames && rPx >= f.quality.minNameRadiusPx;
    f.ctx.globalAlpha = alpha;
    this.labels.draw(
      f,
      d,
      name,
      showName,
      s.showMass,
      this.textCache,
      isOwn ? this.ownMassScale : 0,
    );
    f.ctx.globalAlpha = 1;
  }

  private themeFor(settings: Settings): ArenaTheme {
    const style = settings.style === 'sketch' ? 'sketch' : 'classic';
    const dark = settings.darkMode === true;
    const source = this.opts.assets?.manifest.theme ?? null;
    const t = this.theme;
    if (!t || t.style !== style || t.dark !== dark || this.themeSource !== source) {
      const next = resolveTheme(style, dark, source);
      if (t && t.style !== style) this.jelly.clear();
      this.theme = next;
      this.themeSource = source;
      this.textCache.setThemeColors(next.key, next.colors.nameFill, next.colors.nameOutline);
      return next;
    }
    return t;
  }

  /** World art from the loader, re-read only when the loader's version changes. */
  private artFor(): CachedArt {
    const loader = this.opts.assets ?? null;
    const version = loader ? loader.version : -1;
    const cached = this.art;
    if (cached && cached.loader === loader && cached.version === version) return cached;
    let food: FoodArt | null = null;
    let spikes = NO_SPIKE_ART;
    let ejected: AssetImage | null = null;
    let ejectedTint = true;
    let overlay: AssetImage | null = null;
    let paperLight: AssetImage | null = null;
    let paperDark: AssetImage | null = null;
    if (loader) {
      const images: AssetImage[] = [];
      const tint: boolean[] = [];
      for (let i = 1; i <= PELLET_VARIANTS; i++) {
        const id = `world/pellets/pellet-${String(i).padStart(2, '0')}`;
        const img = loader.userImage(id);
        if (img) {
          images.push(img);
          tint.push(loader.isTinted(id));
        }
      }
      food = images.length > 0 ? { images, tint } : null;
      spikes = {
        virus: loader.userImage('world/virus'),
        virusFed: loader.userImage('world/virus-fed'),
        spawner: loader.userImage('world/spawner'),
      };
      ejected = loader.userImage('world/ejected');
      ejectedTint = loader.isTinted('world/ejected');
      overlay = loader.userImage('world/cell-overlay');
      paperLight = loader.userImage('world/paper-light');
      paperDark = loader.userImage('world/paper-dark');
    }
    // Reading may have started loads that bump the version; key on the version read up front.
    const next: CachedArt = {
      version,
      loader,
      food,
      spikes,
      ejected,
      ejectedTint,
      overlay,
      paperLight,
      paperDark,
    };
    this.art = next;
    return next;
  }

  /** The reused per-frame state (created on the first draw). */
  private frameFor(
    ctx: Ctx2D,
    theme: ArenaTheme,
    quality: RenderQuality,
    settings: Settings,
  ): Frame {
    if (!this.frame || this.frame.ctx !== ctx) {
      this.frame = {
        ctx,
        bs: 1,
        zoom: 1,
        scale: 1,
        offX: 0,
        offY: 0,
        width: 1,
        height: 1,
        view: this.view,
        map: this.map,
        theme,
        quality,
        settings,
        reduceMotion: false,
        nowMs: 0,
        dtMs: 0,
        boilFrame: 0,
        stats: this.stats,
      };
    }
    return this.frame;
  }
}
